import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UsersIcon, 
  CreditCardIcon, 
  HomeIcon, 
  XMarkIcon,
  CogIcon
} from '@heroicons/react/24/outline';

interface UserCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    name: string;
    email: string;
    plan?: string;
  } | null;
  onLogout: () => void;
}

const UserCardModal: React.FC<UserCardModalProps> = ({ isOpen, onClose, user, onLogout }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleLogout = () => {
    onLogout();
    onClose();
  };

  const handleGoHome = () => {
    onClose();
    navigate('/');
  };

  const handleGoSettings = () => {
    onClose();
    navigate('/settings?tab=account');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-25 transition-opacity"
        onClick={onClose}
      />
      
      {/* Desktop Panel */}
      <div className="absolute bottom-0 left-0 mb-16 ml-64 w-80 bg-white rounded-lg shadow-xl border border-gray-200 hidden lg:block">
        <div className="flex flex-col h-full max-h-[600px]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Account</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {/* Navigation Items */}
              <button
                onClick={handleGoSettings}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md group"
              >
                <CogIcon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                Account
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate('/settings?tab=teams');
                }}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md group"
              >
                <UsersIcon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                Teams
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate('/billing');
                }}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md group"
              >
                <CreditCardIcon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                Billing
              </button>
            </div>

            {/* Usage Section */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-900">Usage</span>
                <button
                  onClick={() => {
                    onClose();
                    navigate('/usage');
                  }}
                  className="flex items-center space-x-1 text-xs text-purple-600 hover:text-purple-700 font-medium transition-colors"
                >
                  <span>View Details</span>
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              {/* API Calls */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">API Calls</span>
                  <span className="text-sm font-bold text-gray-900">67/200</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: '33.5%' }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">0</span>
                  <span className="text-xs text-gray-500">200</span>
                </div>
              </div>

              {/* Smart Alerts */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Smart Alerts</span>
                  <span className="text-sm font-bold text-gray-900">1/3</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: '33.3%' }}
                  ></div>
                </div>
              </div>

              {/* Reset info */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500">Monthly limits reset on the 1st</span>
                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">19 days left</span>
              </div>
            </div>

            {/* Upgrade CTA */}
            <div className="mt-4">
              <button
                onClick={() => {
                  onClose();
                  navigate('/pricing');
                }}
                className="w-full px-3 py-2 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-md flex items-center justify-center space-x-1"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
                <span>Upgrade</span>
              </button>
            </div>

            {/* Additional Actions */}
            <div className="mt-2 space-y-1">
              <button
                onClick={handleGoHome}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md group"
              >
                <HomeIcon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                Homepage
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md group"
              >
                <svg className="h-4 w-4 mr-2 text-red-400 group-hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Panel */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-lg shadow-xl border border-gray-200 lg:hidden">
        <div className="flex flex-col h-full max-h-[500px]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Account</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {/* Navigation Items */}
              <button
                onClick={handleGoSettings}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md group"
              >
                <CogIcon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                Account
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate('/settings?tab=teams');
                }}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md group"
              >
                <UsersIcon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                Teams
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate('/billing');
                }}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md group"
              >
                <CreditCardIcon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                Billing
              </button>
            </div>

            {/* Usage Section */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-900">Usage</span>
                <button
                  onClick={() => {
                    onClose();
                    navigate('/usage');
                  }}
                  className="flex items-center space-x-1 text-xs text-purple-600 hover:text-purple-700 font-medium transition-colors"
                >
                  <span>View Details</span>
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              {/* API Calls */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">API Calls</span>
                  <span className="text-sm font-bold text-gray-900">67/200</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: '33.5%' }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">0</span>
                  <span className="text-xs text-gray-500">200</span>
                </div>
              </div>

              {/* Smart Alerts */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Smart Alerts</span>
                  <span className="text-sm font-bold text-gray-900">1/3</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: '33.3%' }}
                  ></div>
                </div>
              </div>

              {/* Reset info */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500">Monthly limits reset on the 1st</span>
                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">19 days left</span>
              </div>
            </div>

            {/* Upgrade CTA */}
            <div className="mt-4">
              <button
                onClick={() => {
                  onClose();
                  navigate('/pricing');
                }}
                className="w-full px-3 py-2 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-md flex items-center justify-center space-x-1"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
                <span>Upgrade</span>
              </button>
            </div>

            {/* Additional Actions */}
            <div className="mt-2 space-y-1">
              <button
                onClick={handleGoHome}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md group"
              >
                <HomeIcon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-gray-500" />
                Homepage
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md group"
              >
                <svg className="h-4 w-4 mr-2 text-red-400 group-hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCardModal;